import * as React from 'react'

import * as hooks from '@owl-nest/hooks'
import * as ds from '@bob/design-system'

export function useInfiniteCarousel<T>({
  data,
  activeIndex,
  elementWidth,
  lengthMultiplier,
  setActiveIndex,
}: {
  data: T[]
  activeIndex?: number
  elementWidth: number
  lengthMultiplier?: number
  setActiveIndex: (index: number) => void
}): {
  isMobile: boolean
  ref: React.RefObject<HTMLUListElement>
  renderedList: (T & { realIndex: number })[]
  onClickElement: (fakeIndex: number, realIndex: number) => void
  onScroll: (e: React.UIEvent<HTMLUListElement>) => void
  onScrollOneStepLeft: () => void
  onScrollOneStepRight: () => void
} {
  const ref = React.useRef<HTMLUListElement>(null)
  const isOnScrollDisabled = React.useRef(false)
  const scrollLeftRef = React.useRef(0)
  const settings = React.useRef<
    | {
        allSetsIndexes: number[][]
        centerIndexes: number[]
        leftIndexes: number[]
        rightIndexes: number[]
      }
    | undefined
  >()

  const [fakeActiveIndex, setFakeActiveIndex] = React.useState<number>()

  const usedLengthMultiplier = getUsedLengthMultiplier(lengthMultiplier, data.length)

  const isClientSide = hooks.useClientSide()
  const isMobile = isClientSide && ds.utils.browser.isMobile()

  const windowWidth = isClientSide && window.innerWidth
  const scrollStep = isMobile ? windowWidth : elementWidth
  const completeScrollStep = scrollStep && scrollStep * data.length

  const renderedList = React.useMemo(
    () =>
      isClientSide
        ? [
            ...Array(usedLengthMultiplier)
              .fill(data)
              .flatMap((list) =>
                list.map((item: T, i: number) => ({
                  ...item,
                  realIndex: i,
                })),
              ),
          ]
        : data,
    [data, usedLengthMultiplier, isClientSide],
  )

  React.useEffect(() => {
    const realInitActiveIndex = getInitIndex()

    const fakeInitActiveIndex = getFakeInitIndex(data.length, realInitActiveIndex, usedLengthMultiplier)

    const allSetsIndexes = [...Array(usedLengthMultiplier)].map((_, index) =>
      [...Array(data.length)].map((_, i) => i + index * data.length),
    )

    const leftIndexes = allSetsIndexes.slice(0, Math.floor(usedLengthMultiplier / 2)).flatMap((i) => i)
    const rightIndexes = allSetsIndexes.slice(Math.floor(usedLengthMultiplier / 2) + 1).flatMap((i) => i)
    const centerIndexes = allSetsIndexes.filter((list) => list.includes(fakeInitActiveIndex)).flatMap((i) => i)
    settings.current = { allSetsIndexes, centerIndexes, leftIndexes, rightIndexes }

    if (windowWidth && scrollStep) {
      if (isMobile) {
        const mobileInitScrollLeft = fakeInitActiveIndex * scrollStep

        scrollList(mobileInitScrollLeft)
        setActiveIndex(realInitActiveIndex)
        setFakeActiveIndex(fakeInitActiveIndex)
      } else {
        const midWindow = windowWidth / 2
        const midLeftWindow = midWindow - scrollStep / 2

        const activeScrollLeft = realInitActiveIndex * scrollStep
        const diffForMid = activeScrollLeft - midLeftWindow

        const stepsFromMiddle = Math.floor(usedLengthMultiplier / 2) * data.length * scrollStep

        const desktopInitScrollLeft = stepsFromMiddle + diffForMid

        scrollList(desktopInitScrollLeft)
        setActiveIndex(realInitActiveIndex)
        setFakeActiveIndex(fakeInitActiveIndex)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isClientSide])

  const debouncedOnScroll = ds.hooks.useDebounce(
    (e) => {
      if (
        windowWidth &&
        scrollStep &&
        completeScrollStep &&
        fakeActiveIndex !== undefined &&
        activeIndex !== undefined
      ) {
        const isOutsideLeft = settings.current?.leftIndexes.includes(fakeActiveIndex)
        const isOutsideRight = settings.current?.rightIndexes.includes(fakeActiveIndex)

        const currentScrollLeft = isMobile ? (e.target as HTMLUListElement).scrollLeft : scrollLeftRef.current

        const targetListIndex =
          settings.current?.allSetsIndexes.findIndex((list) => list.includes(fakeActiveIndex)) ??
          Math.floor(usedLengthMultiplier / 2)

        const fullStepsFromCenter = Math.abs(targetListIndex - Math.floor(usedLengthMultiplier / 2))

        if (isMobile) {
          const isCentered = currentScrollLeft % scrollStep === 0

          if (isCentered) {
            if (isOutsideLeft) {
              const nextScrollStep = currentScrollLeft + completeScrollStep * fullStepsFromCenter

              scrollList(nextScrollStep, 'auto')
              setFakeActiveIndex(fakeActiveIndex + data.length * fullStepsFromCenter)
            }
            if (isOutsideRight) {
              const nextScrollStep = currentScrollLeft - completeScrollStep * fullStepsFromCenter

              scrollList(nextScrollStep, 'auto')
              setFakeActiveIndex(fakeActiveIndex - data.length * fullStepsFromCenter)
            }
          }
        } else {
          if (isOutsideLeft) {
            const nextScrollStep = currentScrollLeft + completeScrollStep * fullStepsFromCenter

            scrollList(nextScrollStep, 'auto')
            setFakeActiveIndex(fakeActiveIndex + data.length * fullStepsFromCenter)
          }
          if (isOutsideRight) {
            const nextScrollStep = currentScrollLeft - completeScrollStep * fullStepsFromCenter

            scrollList(nextScrollStep, 'auto')
            setFakeActiveIndex(fakeActiveIndex - data.length * fullStepsFromCenter)
          }
        }
      }
    },
    false,
    50,
  )

  return {
    isMobile,
    ref,
    renderedList,
    onClickElement,
    onScroll,
    onScrollOneStepLeft,
    onScrollOneStepRight,
  }

  function scrollList(left: number, behavior?: ScrollBehavior) {
    scrollLeftRef.current = left
    ref.current?.scrollTo({
      left,
      behavior,
    })
  }

  function onClickElement(fakeIndex: number, realIndex: number) {
    if (!isMobile) {
      if (fakeActiveIndex !== undefined && scrollStep) {
        if (fakeIndex !== fakeActiveIndex) {
          const nextScrollStep = scrollLeftRef.current - (fakeActiveIndex - fakeIndex) * scrollStep

          scrollList(nextScrollStep, 'smooth')

          setActiveIndex(realIndex)
          setFakeActiveIndex(fakeIndex)
        }
      }
    }
  }

  function onScroll(e: React.UIEvent<HTMLUListElement>) {
    if (isMobile) {
      onMobileScroll((e.target as HTMLUListElement).scrollLeft)
    }

    debouncedOnScroll(e)
  }

  function onMobileScroll(scrollLeft: number) {
    if (activeIndex !== undefined && fakeActiveIndex !== undefined && scrollStep) {
      const step = scrollLeft / scrollStep
      const roundedStep = Math.round(step)

      if (!isOnScrollDisabled.current) {
        if (roundedStep !== fakeActiveIndex) {
          setFakeActiveIndex(roundedStep)
          setActiveIndex(
            step - fakeActiveIndex > 0
              ? activeIndex + 1 > data.length - 1
                ? 0
                : activeIndex + 1
              : activeIndex - 1 < 0
              ? data.length - 1
              : activeIndex - 1,
          )
        }
      } else {
        if (roundedStep === fakeActiveIndex) {
          isOnScrollDisabled.current = false
        }
      }
    }
  }

  function onScrollOneStepLeft() {
    if (fakeActiveIndex !== undefined && activeIndex !== undefined && scrollStep) {
      if (isMobile) {
        isOnScrollDisabled.current = true

        const nextScrollStep = fakeActiveIndex * scrollStep - scrollStep
        scrollList(nextScrollStep, 'smooth')
      } else {
        const nextScrollStep = scrollLeftRef.current - scrollStep

        scrollList(nextScrollStep, 'smooth')
      }

      const nextIndex = activeIndex - 1 < 0 ? data.length - 1 : activeIndex - 1
      setActiveIndex(nextIndex)
      setFakeActiveIndex(fakeActiveIndex - 1)
    }
  }
  function onScrollOneStepRight() {
    if (fakeActiveIndex !== undefined && activeIndex !== undefined && scrollStep) {
      if (isMobile) {
        isOnScrollDisabled.current = true

        const nextScrollStep = fakeActiveIndex * scrollStep + scrollStep
        scrollList(nextScrollStep, 'smooth')
      } else {
        const nextScrollStep = scrollLeftRef.current + scrollStep
        scrollList(nextScrollStep, 'smooth')
      }

      const nextIndex = activeIndex + 1 > data.length - 1 ? 0 : activeIndex + 1
      setActiveIndex(nextIndex)
      setFakeActiveIndex(fakeActiveIndex + 1)
    }
  }
}

function getInitIndex(): number {
  return 0
}

function getFakeInitIndex(dataLength: number, realActiveIndex: number, usedLengthMultiplier: number) {
  return realActiveIndex + Math.floor(usedLengthMultiplier / 2) * dataLength
}

function getUsedLengthMultiplier(lengthMultiplier: number | undefined, length: number): number {
  if (lengthMultiplier) return lengthMultiplier
  if (length < 4) return 14
  if (length < 5) return 11
  if (length < 6) return 8
  if (length < 8) return 7
  if (length < 10) return 6
  return 5
}
